"use client";

import { safeNextPath } from "@/lib/auth/safe-next";
import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { withCampaignParams } from "@/lib/attribution/campaign-params";
import { AtSign, Lock, LogIn } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { AuthInput } from "@/components/auth/AuthInput";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { createClient } from "@/lib/supabase/client";
import OtpInput from "@/components/ui/OtpInput";
import { SIGNUP_CODE_LENGTH, isCompleteSignupCode } from "@/lib/auth/signup-code";
import { classifyAuthError } from "@/lib/supabase/auth-error-copy";
import { isNative } from "@/lib/native/is-native";
import { hasIapPlugin } from "@/lib/native/iap";

function validateEmail(email: string): string | null {
  if (!email) return "Enter your email.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return "That doesn't look like a valid email.";
  return null;
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showResend, setShowResend] = useState(false);
  // The six boxes, opened only once a code has actually been sent — an empty
  // code field on a page nobody has been emailed is a question with no answer.
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams.get("next");

  // Only show the "Sign up" CTA on the web or on an IAP-capable binary. Pre-IAP
  // binaries stay sign-in-only (they can't run the purchase flow). Resolved
  // after mount so SSR markup matches the initial client render.
  const [showSignupCta, setShowSignupCta] = useState(true);
  useEffect(() => {
    setShowSignupCta(!isNative() || hasIapPlugin());
  }, []);

  // Has React hydrated? Until it has, `onSubmit={handleSubmit}` is not attached
  // and a click falls through to the native `method="post"` below.
  //
  // That fallback stops a pre-hydration submit LEAKING the password into the
  // URL (see the long comment on the <form>), but it cannot make the submit
  // WORK: nothing handles POST /login, so the browser navigates, the page
  // re-renders, and the user is left looking at an empty form. They are told
  // nothing at all — `serverError` is React state, so the navigation wipes any
  // message along with the fields.
  //
  // Seen for real, repeatedly, in CI: a click that beat hydration produced two
  // navigations to /login and a snapshot with both inputs blank. Our own trades
  // customers are the population most exposed to it — engineers on van signal,
  // where the gap between paint and hydration is widest.
  //
  // So don't let that click happen. Enabling the button on hydration keeps the
  // POST fallback as defence-in-depth while removing the only route to it that
  // a normal user can take. It also covers Enter-to-submit: implicit form
  // submission is a no-op when the form's default button is disabled.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    // Detecting hydration is the one case where setting state in an effect IS
    // the pattern — the effect running at all is precisely the signal. The
    // directive has to sit immediately above the call: `disable-next-line`
    // applies to the very next line, so a multi-line reason placed after it
    // disables nothing and earns an "unused directive" warning instead.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (searchParams.get("reset") === "success") {
      setInfo("Password updated. Sign in with your new password.");
    }
    if (searchParams.get("error") === "invalid_link") {
      setServerError("That link has expired or is invalid. Please try again.");
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError("");
    setInfo("");
    setShowResend(false);

    const next: Record<string, string> = {};
    const emailErr = validateEmail(email.trim());
    if (emailErr) next.email = emailErr;
    if (!password) next.password = "Enter your password.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);

    // Per-email rate limit precheck. Supabase Auth already rate-limits by IP
    // (10/5min) — this layer adds a per-email check so a single bad actor on
    // a CGNAT'd network can't burn through the IP budget for everyone else.
    // Fails open if the precheck endpoint is unreachable (KV down, etc.) —
    // the IP-based Supabase limit is still in force.
    const normalisedEmail = email.trim().toLowerCase();
    try {
      const pre = await fetch("/api/auth/login-precheck", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalisedEmail }),
      });
      if (pre.status === 429) {
        const data = (await pre.json().catch(() => null)) as
          | { message?: string }
          | null;
        setServerError(
          data?.message ??
            "Too many sign-in attempts. Try again in a few minutes.",
        );
        setLoading(false);
        return;
      }
    } catch {
      // Network blip hitting our own endpoint — let the actual Supabase call
      // run and surface a meaningful error from there.
    }

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      const errName = error.name ?? "";
      const errMsg = (error.message ?? "").toLowerCase();
      const isUnconfirmed = errMsg.includes("email not confirmed");
      const isNetwork =
        errName === "AuthRetryableFetchError" ||
        errMsg === "load failed" ||
        errMsg.includes("failed to fetch") ||
        errMsg.includes("networkerror");

      // Record the failure for the per-email bucket — but only when Supabase
      // actually replied with an auth verdict. A network blip isn't a sign-in
      // attempt and shouldn't count toward the lockout.
      if (!isNetwork) {
        void fetch("/api/auth/login-record-failure", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalisedEmail }),
          keepalive: true,
        }).catch(() => { /* best effort */ });
      }

      const { display } = classifyAuthError(error, "login", [
        {
          match: (m) =>
            m.includes("invalid login credentials") ||
            m.includes("invalid_credentials"),
          display: "Wrong email or password.",
        },
        {
          match: (m) => m.includes("email not confirmed"),
          display: "You need to verify your email first \u2014 we'll send you a code.",
        },
      ]);
      setServerError(display);
      if (isUnconfirmed) setShowResend(true);
      setLoading(false);
      return;
    }
    // Demo environment: reset the workspace before the dashboard loads, so
    // every demo starts pristine (wipe + reseed, date-shifted to today).
    // Best-effort with a hard cap — a slow/failed reset must never strand
    // the login. See src/lib/demo/reset.ts.
    if (process.env.NEXT_PUBLIC_APP_ENV === "demo") {
      try {
        await fetch("/api/demo/reset", {
          method: "POST",
          signal: AbortSignal.timeout(15_000),
        });
      } catch {
        // Logged server-side; proceed with whatever state the workspace is in.
      }
    }

    // Never the raw param: router.push follows an absolute URL off-site.
    //
    // 🚨 And no `router.refresh()` after it. Next queues a refresh behind the
    // navigation and the screen waits for the LATEST router state, so the
    // sign-in could not draw until a second fetch of the same page came back:
    // an iPhone had /settings in hand at 5s and was still on this form at 20s
    // (MUCKA-WEB-CQ). The push already renders everything below the root
    // layout with the new session. Test: signing-in-moves-the-page-in-one-round-trip.
    router.push(safeNextPath(searchParams.get("next")));
  }

  async function handleResend() {
    setResending(true);
    const supabase = createClient();
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback?type=signup`,
      },
    });
    if (error) {
      const { display } = classifyAuthError(error, "signup-resend");
      setServerError(display);
    } else {
      setInfo("We've sent a code to your email. Pop it in below.");
      setShowResend(false);
      // 🚨 Open the boxes HERE, not on a screen of their own. Somebody who has
      // just been told to check their inbox and is given nowhere to put what
      // they find is where this flow used to end: two of the last seven signups
      // on 14 September 2026 never confirmed and never got in at all.
      setShowCode(true);
    }
    setResending(false);
  }

  /*
   * Verifying an unconfirmed address from the LOGIN screen.
   *
   * Same token, same `type: 'signup'`, same browser client as the signup
   * screen's — see the long comment on `handleVerify` in SignupClient. This
   * exists because the person who never confirmed does not come back to
   * /signup, they come back to /login and are told they cannot come in.
   */
  async function handleVerifyCode(fullCode: string) {
    if (!isCompleteSignupCode(fullCode) || verifyingCode) return;
    setVerifyingCode(true);
    setCodeError(null);
    const supabase = createClient();
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: fullCode,
      type: "signup",
    });
    if (error) {
      const msg = (error.message ?? "").toLowerCase();
      setCodeError(
        msg.includes("expired")
          ? "That code has expired. Send a new one and try again."
          : msg.includes("invalid")
            ? "That code isn't right. Give it another go."
            : error.message,
      );
      setVerifyingCode(false);
      return;
    }
    window.location.assign("/");
  }

  return (
    <>
      <h1 className="t-display-lg mucka-auth-title">Welcome back</h1>
      <p className="t-body-md mucka-auth-sub">
        Log in to your account and get cracking.
      </p>

      {info && <div className="mucka-auth-banner mucka-auth-banner--info">{info}</div>}
      {serverError && (
        <div className="mucka-auth-banner mucka-auth-banner--error">
          {serverError}
          {showResend && (
            <>
              {" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="mucka-auth-banner-link"
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                {resending ? "Sending…" : "Send me a code"}
              </button>
            </>
          )}
        </div>
      )}

      {showCode && (
        <div className="mucka-auth-cta" style={{ marginTop: 4, marginBottom: 18 }}>
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              if (codeError) setCodeError(null);
            }}
            onComplete={handleVerifyCode}
            length={SIGNUP_CODE_LENGTH}
            disabled={verifyingCode}
            label="Verification code"
          />
          {codeError && (
            <p className="mucka-onb-error" role="alert" style={{ marginTop: 10 }}>
              {codeError}
            </p>
          )}
          {verifyingCode && (
            <p className="t-body-sm mucka-auth-sub" role="status" style={{ marginTop: 10 }}>
              Checking&hellip;
            </p>
          )}
        </div>
      )}

      {/*
        method="post" is a SECURITY fallback, not a routing choice. handleSubmit
        calls preventDefault, so this method is never used once React has
        hydrated — it only matters when the handler is not attached yet.

        Without it the browser default is GET, and these inputs carry
        name="username" / name="password" (needed for password managers). So a
        submit before hydration — slow connection, a JS error, a chunk that
        failed to load — sends the user's password in the query string, where it
        lands in server access logs, browser history and any referrer. Observed
        for real: a submit on a non-hydrated login page produced
        `GET /login?username=…&password=…` with the password in plain text.

        POSTing to a page route is not "handled" — it just doesn't quietly write
        credentials into a URL, which is the whole point. A visible failure beats
        a silent leak.
      */}
      <form className="mucka-auth-form" method="post" onSubmit={handleSubmit} noValidate>
        <AuthInput
          icon={AtSign}
          type="email"
          inputMode="email"
          name="username"
          autoComplete="username"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />
        <AuthInput
          icon={Lock}
          type="password"
          passwordToggle
          name="password"
          autoComplete="current-password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />

        <a className="mucka-auth-forgot" href="/auth/reset">
          Forgot password?
        </a>

        <div className="mucka-auth-cta">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            leadingIcon={LogIn}
            disabled={loading || !hydrated}
          >
            {loading ? "Signing in…" : "Log In"}
          </Button>

          {process.env.NEXT_PUBLIC_ENABLE_OAUTH === "true" && (
            <>
              <div className="mucka-auth-divider">or</div>
              <OAuthButtons next={next} intent="login" />
            </>
          )}
        </div>
      </form>

      {showSignupCta ? (
        <p className="mucka-auth-foot">
          Don&apos;t have an account?
          {/* Carries an ad click's campaign tags on to signup, which reads them off its own URL. */}
          <a href={withCampaignParams("/signup", searchParams, { next })}>Sign up</a>
        </p>
      ) : (
        <p className="mucka-auth-foot">No account yet? Get set up at mucka.ai</p>
      )}

      <p className="mucka-auth-legal">
        By using this app, you agree to our{" "}
        <a href="https://mucka.ai/privacy/" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://mucka.ai/terms-of-use/" target="_blank" rel="noopener noreferrer">
          Terms of Use
        </a>
        .
      </p>
    </>
  );
}

export default function LoginPage() {
  return (
    <div className="mucka-auth-page mucka-auth-page--login">
      <div className="mucka-auth-container">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/mucka-orange-logo.png" alt="Mucka" className="mucka-auth-logo" />
        <Suspense>
          <LoginForm />
        </Suspense>
      </div>
    </div>
  );
}
